// Tracks the Super-Admin "View as" session: which Personnel record is being
// impersonated and whether writes are allowed. The selection is persisted in
// localStorage so it survives reloads, and the axios interceptor
// (authedAxios.js) reads the same keys to stamp every request with the
// X-View-As / X-View-As-Write headers. The backend ignores those headers unless
// the caller is genuinely a Super Admin, so this can never grant extra access.
//
// Changing the selection here re-fetches GET /me (MeContext depends on the
// `token` below), so the whole UI re-gates as the impersonated person.
import { createContext, useContext, useMemo, useState } from "react";

export const VIEW_AS_ID_KEY = "viewAsPersonnelId";
export const VIEW_AS_WRITE_KEY = "viewAsAllowWrites";

const ViewAsContext = createContext(null);

export function ViewAsProvider({ children }) {
  const [personnelId, setPersonnelId] = useState(
    () => localStorage.getItem(VIEW_AS_ID_KEY) || null);
  const [allowWrites, setAllowWrites] = useState(
    () => localStorage.getItem(VIEW_AS_WRITE_KEY) === "true");

  const value = useMemo(() => {
    // Start impersonating `id`. `writes` opts into (audited) write access.
    const start = (id, writes = false) => {
      localStorage.setItem(VIEW_AS_ID_KEY, String(id));
      localStorage.setItem(VIEW_AS_WRITE_KEY, writes ? "true" : "false");
      setPersonnelId(String(id));
      setAllowWrites(!!writes);
    };
    // Toggle write access on the *current* session without re-selecting the
    // person, so you can switch read-only <-> writeable in place.
    const setWrites = (writes) => {
      localStorage.setItem(VIEW_AS_WRITE_KEY, writes ? "true" : "false");
      setAllowWrites(!!writes);
    };
    const stop = () => {
      localStorage.removeItem(VIEW_AS_ID_KEY);
      localStorage.removeItem(VIEW_AS_WRITE_KEY);
      setPersonnelId(null);
      setAllowWrites(false);
    };
    return {
      personnelId,
      allowWrites,
      active: personnelId != null,
      // A value that changes whenever the session changes, for effect deps.
      token: personnelId ? `${personnelId}:${allowWrites}` : "",
      start,
      setWrites,
      stop,
    };
  }, [personnelId, allowWrites]);

  return <ViewAsContext.Provider value={value}>{children}</ViewAsContext.Provider>;
}

export function useViewAs() {
  const ctx = useContext(ViewAsContext);
  if (!ctx) throw new Error("useViewAs must be used within a ViewAsProvider");
  return ctx;
}
